import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { atPlanLimit, planFeatures } from '../utils/planFeatures';
import ConfirmDialog from '../components/ConfirmDialog';
import './Disciplines.css';

const Disciplines = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editingDiscipline, setEditingDiscipline] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '⚽',
    color: '#3498db',
    selectedSport: '' // Para el combobox
  });

  const queryClient = useQueryClient();

  // Obtener lista de disciplinas
  const { data: disciplines, isLoading } = useQuery(
    'disciplines',
    async () => {
      const response = await api.get('/disciplines');
      return response.data;
    }
  );

  // Mutación para crear disciplina
  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/disciplines', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Disciplina creada exitosamente');
        queryClient.invalidateQueries('disciplines');
        handleCloseModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Error al crear disciplina');
      }
    }
  );

  // Mutación para actualizar disciplina
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/disciplines/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Disciplina actualizada exitosamente');
        queryClient.invalidateQueries('disciplines');
        handleCloseModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Error al actualizar disciplina');
      }
    }
  );

  // Mutación para desactivar/activar disciplina
  const toggleActiveMutation = useMutation(
    async ({ id, isActive }) => {
      const response = await api.put(`/disciplines/${id}`, { is_active: !isActive });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Estado de disciplina actualizado');
        queryClient.invalidateQueries('disciplines');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Error al actualizar estado');
      }
    }
  );

  const handleOpenModal = (discipline = null) => {
    if (discipline) {
      setEditingDiscipline(discipline);
      // Verificar si el nombre existe en la lista de deportes
      const isInList = sportsOptions.includes(discipline.name);
      setFormData({
        name: discipline.name || '',
        description: discipline.description || '',
        icon: discipline.icon || '⚽',
        color: discipline.color || '#3498db',
        selectedSport: isInList ? discipline.name : 'Otro'
      });
    } else {
      setEditingDiscipline(null);
      setFormData({
        name: '',
        description: '',
        icon: '⚽',
        color: '#3498db',
        selectedSport: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDiscipline(null);
    setFormData({
      name: '',
      description: '',
      icon: '⚽',
      color: '#3498db',
      selectedSport: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar que haya un nombre
    if (!formData.name || formData.name.trim() === '') {
      toast.error('El nombre del deporte es obligatorio');
      return;
    }
    
    // Preparar datos para enviar (sin selectedSport)
    const { selectedSport, ...dataToSend } = formData;
    
    if (editingDiscipline) {
      updateMutation.mutate({ id: editingDiscipline.id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  const handleToggleActive = (discipline) => {
    const action = discipline.is_active ? 'desactivar' : 'activar';
    setConfirmDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Disciplina`,
      message: `¿Estás seguro de ${action} esta disciplina?`,
      onConfirm: () => {
        toggleActiveMutation.mutate({ id: discipline.id, isActive: discipline.is_active });
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancelar',
    });
  };

  const iconOptions = ['⚽', '🏀', '🏐', '🎾', '🏊', '🏋️', '🤸', '🥋', '🥊', '🏃', '🚴', '🏇'];
  
  // Lista de deportes predefinidos
  const sportsOptions = [
    'Natación',
    'Fútbol',
    'Baloncesto',
    'Voleibol',
    'Tenis',
    'Karate',
    'Taekwondo',
    'Judo',
    'Boxeo',
    'Atletismo',
    'Ciclismo',
    'Gimnasia',
    'Ballet',
    'Danza',
    'Yoga',
    'Pilates',
    'Crossfit',
    'Fitness',
    'Hockey',
    'Rugby',
    'Waterpolo',
    'Patinaje',
    'Escalada',
    'Artes Marciales',
    'Otro'
  ];

  const maxDisciplines = planFeatures(user).max_disciplines;
  const activeDisciplines = (disciplines || []).filter((item) => item.is_active !== false).length;
  const disciplineLimitReached = atPlanLimit(activeDisciplines, maxDisciplines);

  if (isLoading) {
    return <div className="loading">Cargando disciplinas...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Gestión de Disciplinas</h2>
        <button 
          className="btn btn-primary" 
          onClick={() => handleOpenModal()}
          disabled={disciplineLimitReached}
          title={disciplineLimitReached ? 'El plan Free permite 1 disciplina' : undefined}
        >
          + Nueva Disciplina
        </button>
      </div>
      {disciplineLimitReached && (
        <p className="plan-hint">El plan Free permite hasta 1 disciplina.</p>
      )}

      <div className="card">
        {disciplines && disciplines.length > 0 ? (
          <div className="disciplines-grid">
            {disciplines.map((discipline) => (
              <div 
                key={discipline.id} 
                className={`discipline-card ${!discipline.is_active ? 'inactive' : ''}`}
                style={{ borderLeftColor: discipline.color || '#3498db' }}
              >
                <div className="discipline-header">
                  <div className="discipline-icon" style={{ color: discipline.color || '#3498db' }}>
                    {discipline.icon || '⚽'}
                  </div>
                  <div className="discipline-info">
                    <h3>{discipline.name}</h3>
                    <span className={`badge ${discipline.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {discipline.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                </div>
                {discipline.description && (
                  <p className="discipline-description">{discipline.description}</p>
                )}
                <div className="discipline-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleOpenModal(discipline)}
                  >
                    Editar
                  </button>
                  <button
                    className={`btn btn-sm ${discipline.is_active ? 'btn-warning' : 'btn-success'}`}
                    onClick={() => handleToggleActive(discipline)}
                    disabled={toggleActiveMutation.isLoading}
                  >
                    {discipline.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No hay disciplinas registradas</p>
            <button 
              className="btn btn-primary" 
              onClick={() => handleOpenModal()}
            >
              Crear primera disciplina
            </button>
          </div>
        )}
      </div>

      {/* Modal para crear/editar */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingDiscipline ? 'Editar Disciplina' : 'Nueva Disciplina'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Deporte *</label>
                <select
                  value={formData.selectedSport}
                  onChange={(e) => {
                    const selected = e.target.value;
                    setFormData({ 
                      ...formData, 
                      selectedSport: selected,
                      name: selected === 'Otro' ? '' : selected
                    });
                  }}
                  required
                >
                  <option value="">Seleccionar deporte...</option>
                  {sportsOptions.map((sport) => (
                    <option key={sport} value={sport}>
                      {sport}
                    </option>
                  ))}
                </select>
                {formData.selectedSport === 'Otro' && (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Escribe el nombre del deporte"
                    style={{ marginTop: '10px' }}
                  />
                )}
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción de la disciplina"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Icono</label>
                <div className="icon-selector">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      className={`icon-option ${formData.icon === icon ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, icon })}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Color</label>
                <div className="color-selector">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  />
                  <span className="color-preview" style={{ backgroundColor: formData.color }}>
                    {formData.color}
                  </span>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                >
                  {createMutation.isLoading || updateMutation.isLoading
                    ? 'Guardando...'
                    : editingDiscipline
                    ? 'Actualizar'
                    : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onClose={confirmDialog.onCancel}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
        />
      )}
    </div>
  );
};

export default Disciplines;
